'use client'

import { ButtonActionType, type InteractiveButton } from '@/types/interactiveVideo'
import { ArrowRight, ChevronDown, ExternalLink } from 'lucide-react'
import { FC, useEffect, useRef, useState } from 'react'
import { resolveBranchButtonStyle } from '../branchButtonStyle'
import WideSurveyEntryButton from './WideSurveyEntryButton'
import { WIDE_FONT_FAMILY } from './wideFont'

export type WideBranchOverlayProps = {
  buttons: InteractiveButton[]
  /** 選択肢の上に出す問いかけ。未設定なら文言を出さずボタンだけ見せる */
  questionText?: string | null
  defaultButtonColor?: string | null
  /** 動画下部のクレジット帯などを避ける余白。幕（グラデーション）は最下端まで敷き、
   *  中身だけをこのぶん上に逃がす */
  bottomOffset?: number
  /** BOX型PCモーダル向けの縦積み・白背景表示 */
  variant?: 'default' | 'box'
  /**
   * 指定すると見出し右側に選択肢を閉じるボタンを出す。BOX は再生中も選択肢を開けるので、
   * 開いたあと映像へ戻れる出口が要る。ワイド型は動画が終わってから出すため渡さない。
   */
  onCollapse?: () => void
  /**
   * 任意アンケートの入口。選択肢が出ている間は下部のアンケート帯を隠すので、代わりに
   * 選択肢の上・右端に小さなボタンを出す（選択肢を「最初」から出す動画でも入口が消えないように）。
   */
  surveyEntry?: { total: number; onOpen: () => void } | null
  onSelect: (buttonId: string) => void | Promise<void>
}

// 選択肢の後ろの幕。BOX は従来の濃さ。
const BOX_SCRIM_GRADIENT =
  'linear-gradient(180deg, rgba(8,18,38,0) 0%, rgba(8,18,38,0.46) 34%, rgba(8,18,38,0.9) 72%, rgba(8,18,38,0.97) 100%)'
// ワイド型は濃さを少しずつ変え（イージンググラデーション）、白い映像でも幕の境目が見えないようにする。
const WIDE_SCRIM_GRADIENT =
  'linear-gradient(180deg, rgba(8,18,38,0) 0%, rgba(8,18,38,0.03) 12%, rgba(8,18,38,0.1) 24%, rgba(8,18,38,0.22) 36%, rgba(8,18,38,0.38) 48%, rgba(8,18,38,0.55) 60%, rgba(8,18,38,0.7) 72%, rgba(8,18,38,0.8) 84%, rgba(8,18,38,0.85) 100%)'

export const BranchButton: FC<{
  button: InteractiveButton
  index: number
  compact: boolean
  variant: 'default' | 'box'
  defaultButtonColor?: string | null
  onSelect: (buttonId: string) => void | Promise<void>
}> = ({ button, index, compact, variant, defaultButtonColor, onSelect }) => {
  const [isHover, setIsHover] = useState(false)
  const { backgroundColor, color } = resolveBranchButtonStyle(button, defaultButtonColor, isHover)
  const isBox = variant === 'box'
  const isExternalLink = button.type === ButtonActionType.EXTERNAL_LINK
  const BoxIcon = isExternalLink ? ExternalLink : ArrowRight

  return (
    <button
      type="button"
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      onFocus={() => setIsHover(true)}
      onBlur={() => setIsHover(false)}
      onClick={() => void onSelect(button.id)}
      style={{
        display: isBox ? 'grid' : 'flex',
        gridTemplateColumns: isBox ? '22px minmax(0, 1fr) 22px' : undefined,
        minWidth: 0,
        minHeight: isBox ? '42px' : compact ? '38px' : '44px',
        width: isBox ? undefined : '100%',
        flex: 'none',
        alignItems: 'center',
        gap: compact ? '8px' : '10px',
        padding: compact ? '7px 10px' : '9px 13px',
        border: isBox
          ? isExternalLink
            ? '1px solid rgba(255,255,255,0.45)'
            : isHover
              ? '1px solid #D1D5DB'
              : '1px solid rgba(255,255,255,0.88)'
          : isHover
            ? '1px solid color-mix(in srgb, var(--video-brand-color, var(--video-accent-color)) 55%, transparent)'
            : '1px solid rgba(255,255,255,0.22)',
        borderRadius: isBox ? '999px' : compact ? '9px' : '11px',
        backgroundColor: isBox ? (isExternalLink ? backgroundColor : isHover ? '#F3F4F6' : '#fff') : backgroundColor,
        color: isBox ? (isExternalLink ? color : '#172033') : color,
        fontFamily: 'inherit',
        fontSize: isBox ? '16px' : compact ? '11px' : '13px',
        fontWeight: 750,
        lineHeight: 1.35,
        textAlign: 'left',
        cursor: 'pointer',
        boxShadow: isHover ? '0 10px 24px -14px rgba(0,0,0,0.72)' : 'none',
        transform: isHover ? 'translateY(-1px)' : 'translateY(0)',
        backdropFilter: 'blur(10px)',
        transition: 'background-color 180ms ease, border-color 180ms ease, transform 180ms ease, box-shadow 180ms ease',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          width: compact ? '22px' : '26px',
          height: compact ? '22px' : '26px',
          flexShrink: 0,
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: isBox ? '50%' : '7px',
          background: isBox
            ? isExternalLink
              ? 'rgba(255,255,255,0.18)'
              : isHover
                ? '#E5E7EB'
                : '#F3F4F6'
            : isHover
              ? 'color-mix(in srgb, var(--video-brand-color, var(--video-accent-color)) 16%, white)'
              : 'color-mix(in srgb, var(--video-brand-color, var(--video-accent-color)) 8%, white)',
          color: isBox
            ? isExternalLink
              ? color
              : '#4B5563'
            : 'var(--video-brand-color, var(--video-accent-color))',
          fontSize: isBox ? '11px' : compact ? '9px' : '10px',
          fontWeight: 850,
          boxShadow: isBox
            ? undefined
            : 'inset 0 0 0 1px color-mix(in srgb, var(--video-brand-color, var(--video-accent-color)) 18%, transparent)',
        }}
      >
        {String(index + 1).padStart(2, '0')}
      </span>
      <span
        style={{
          minWidth: 0,
          flex: 1,
          overflow: 'hidden',
          textAlign: isBox ? 'center' : 'left',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {button.label}
      </span>
      <BoxIcon
        size={isBox ? 16 : compact ? 13 : 15}
        style={{
          flexShrink: 0,
          justifySelf: isBox ? 'center' : undefined,
          color: isBox && !isExternalLink ? '#4B5563' : undefined,
          opacity: isHover ? 1 : 0.72,
        }}
      />
    </button>
  )
}

/**
 * WIDE A: 最終フレームはそのまま見せ、下部だけに浅いグラデーションと横並びの
 * 選択肢を置く。映像全体を暗転させないので、登壇者や画面内の情報を遮らない。
 */
const WideBranchOverlay: FC<WideBranchOverlayProps> = ({
  buttons,
  questionText,
  defaultButtonColor,
  bottomOffset = 0,
  variant = 'default',
  onCollapse,
  surveyEntry,
  onSelect,
}) => {
  const displayButtons = buttons.slice(0, 4)
  const overlayRef = useRef<HTMLDivElement>(null)
  const [isCompact, setIsCompact] = useState(false)
  const [isCollapseHovered, setIsCollapseHovered] = useState(false)
  const isBox = variant === 'box'

  useEffect(() => {
    const element = overlayRef.current
    if (!element) return

    const sync = () => setIsCompact(element.getBoundingClientRect().width < 560)
    sync()
    const observer = new ResizeObserver(sync)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  if (displayButtons.length === 0) return null

  return (
    <div
      ref={overlayRef}
      data-testid="wide-branch-overlay"
      style={{
        position: 'absolute',
        right: 0,
        // 幕は必ず動画の最下端まで敷く。bottom を上げるとクレジット帯の高さぶん
        // グラデーションが途中で切れ、幕の下端に明るい映像が帯状に残ってしまう。
        bottom: 0,
        left: 0,
        zIndex: 30,
        display: 'flex',
        // ワイド型は幕を低めにして映像を隠しすぎない（BOX は従来どおり）
        minHeight: isCompact ? '44%' : isBox ? '28%' : '22%',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        gap: isCompact ? '7px' : '10px',
        // 下端の余白だけ bottomOffset を足し、選択肢がクレジット・シークバーに被らないようにする
        padding: isCompact
          ? `30px 10px ${4 + bottomOffset}px`
          : `${isBox ? 54 : 40}px 24px ${(isBox ? 17 : 16) + bottomOffset}px`,
        background: isBox ? BOX_SCRIM_GRADIENT : WIDE_SCRIM_GRADIENT,
        color: '#fff',
        fontFamily: WIDE_FONT_FAMILY,
        pointerEvents: 'none',
        animation: 'moovy-wide-branch-in 260ms ease-out',
      }}
    >
      <style>{`@keyframes moovy-wide-branch-in { from { opacity: 0; transform: translateY(8px) } to { opacity: 1; transform: translateY(0) } }`}</style>

      {/* 任意アンケートの入口。質問文があればその行の右端に並べ（選択肢に近い位置まで下げる）、
          無ければ選択肢の列のすぐ上・右端に置く */}
      {surveyEntry && !questionText && (
        <WideSurveyEntryButton
          total={surveyEntry.total}
          onOpen={surveyEntry.onOpen}
          style={{ alignSelf: 'flex-end' }}
        />
      )}

      {(questionText || onCollapse) && (
        <div
          style={{
            position: 'relative',
            display: 'flex',
            width: isBox ? 'fit-content' : undefined,
            // 入口を右端に並べるときは行を全幅にする
            maxWidth: surveyEntry ? 'none' : '760px',
            minHeight: onCollapse ? '32px' : undefined,
            alignItems: 'center',
            justifyContent: isBox ? 'center' : 'flex-start',
            gap: surveyEntry ? '12px' : isBox && onCollapse ? '8px' : undefined,
            alignSelf: isBox ? 'center' : undefined,
            paddingRight: !isBox && onCollapse ? '38px' : 0,
          }}
        >
          {questionText && (
            <p
              style={{
                overflow: 'hidden',
                flex: surveyEntry ? 1 : undefined,
                minWidth: 0,
                margin: 0,
                width: isBox ? 'auto' : '100%',
                color: '#fff',
                fontSize: isBox ? '14px' : isCompact ? '12px' : '16px',
                fontWeight: 780,
                lineHeight: 1.4,
                letterSpacing: '-0.01em',
                textOverflow: 'ellipsis',
                textShadow: '0 2px 10px rgba(2,6,23,0.62)',
                textAlign: isBox ? 'center' : 'left',
                whiteSpace: 'nowrap',
              }}
            >
              {questionText}
            </p>
          )}

          {surveyEntry && questionText && (
            <WideSurveyEntryButton total={surveyEntry.total} onOpen={surveyEntry.onOpen} style={{ flexShrink: 0 }} />
          )}

          {onCollapse && (
            <button
              type="button"
              onClick={onCollapse}
              onMouseEnter={() => setIsCollapseHovered(true)}
              onMouseLeave={() => setIsCollapseHovered(false)}
              onFocus={() => setIsCollapseHovered(true)}
              onBlur={() => setIsCollapseHovered(false)}
              aria-label="選択肢を閉じて動画に戻る"
              title="動画に戻る"
              data-testid="wide-branch-collapse"
              style={{
                position: isBox ? 'relative' : 'absolute',
                right: isBox ? undefined : 0,
                flexShrink: 0,
                display: 'grid',
                width: '32px',
                height: '32px',
                placeItems: 'center',
                padding: 0,
                border: isCollapseHovered ? '1px solid rgba(255,255,255,0.5)' : '1px solid rgba(255,255,255,0.28)',
                borderRadius: '8px',
                background: isCollapseHovered ? 'rgba(107,114,128,0.72)' : 'rgba(255,255,255,0.12)',
                color: '#fff',
                fontFamily: 'inherit',
                cursor: 'pointer',
                pointerEvents: 'auto',
                transition: 'background-color 160ms ease, border-color 160ms ease',
              }}
            >
              <ChevronDown size={18} strokeWidth={2.5} aria-hidden="true" />
            </button>
          )}
        </div>
      )}

      <div
        style={{
          display: isBox ? 'flex' : 'grid',
          width: '100%',
          maxWidth: isBox ? '420px' : 'none',
          gridTemplateColumns: isBox ? undefined : `repeat(${displayButtons.length}, minmax(0, 1fr))`,
          flexDirection: isBox ? 'column' : 'row',
          flexWrap: 'nowrap',
          // 選択肢どうしの間隔は BOX・ワイド型とも 8px で揃える。
          // 幅（isCompact）で詰めていたが、狭い枠ほどボタンが接近して押し間違えやすかった。
          gap: '8px',
          alignSelf: isBox ? 'center' : undefined,
          pointerEvents: 'auto',
        }}
      >
        {displayButtons.map((button, index) => (
          <BranchButton
            key={button.id}
            button={button}
            index={index}
            compact={isCompact}
            variant={variant}
            defaultButtonColor={defaultButtonColor}
            onSelect={onSelect}
          />
        ))}
      </div>
    </div>
  )
}

export default WideBranchOverlay
